import { Grid, Typography, Button, Box, Skeleton } from "@mui/material";
import { ThreeDots } from "react-loader-spinner";
import React, { useState, useEffect } from "react";
import useProjects from "../../../hooks/useProjects";

export default function QrUploadSection({
  projectDetails,
  qrLogoFile,
  setQrPreviewUrl,
  qrPreviewUrl,
  setIsQrModalOpen,
  projectId,
  setQrIcon
}) {

  const {
    uploadQrIcon,
    qrIcon,
    isQrIconSaved,
    uploadingQrIcon,
    removeQrIcon,
    removingQrIcon,
    isQrIconRemoved,
  } = useProjects();

  const [isEditing, setIsEditing] = useState(false);
  const [localPreview, setLocalPreview] = useState(null);
  const [error, setError] = useState(false);
  const [isProjectLoading, setIsProjectLoading] = useState(true);
  const [hasExistingQr, setHasExistingQr] = useState(false);
  const [qrLogoFileState, setQrLogoFileState] = useState(null);

  useEffect(() => {
      if(isQrIconSaved && qrIcon){
          setQrIcon(qrIcon);
      }
    },[isQrIconSaved, qrIcon])

  // ✅ Detect when projectDetails are ready
  useEffect(() => {
    if (projectDetails) {
      setIsProjectLoading(false);
      setHasExistingQr(!!projectDetails.qr_icon_url);
    }
  }, [projectDetails]);

  // ✅ Handle successful upload (switch to view mode)
  useEffect(() => {
    if (isQrIconSaved) {
      setIsEditing(false);
      setLocalPreview(null);
      setQrPreviewUrl(null);
      setError(false);
      setHasExistingQr(true);
    }
  }, [isQrIconSaved]);

  // ✅ Handle successful remove (switch to browse + disabled save)
  useEffect(() => {
    if (isQrIconRemoved) {
      setIsEditing(false);
      setLocalPreview(null);
      setQrPreviewUrl(null);
      setError(false);
      setHasExistingQr(false);
    }
  }, [isQrIconRemoved]);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const previewURL = URL.createObjectURL(file);
      setLocalPreview(previewURL);
      setQrPreviewUrl(previewURL);
      setQrLogoFileState(file);
    }
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setLocalPreview(null);
    setQrPreviewUrl(null);
    setError(false);
  };

  const handleCancelClick = () => {
    setIsEditing(false);
    setLocalPreview(null);
    setQrPreviewUrl(null);
    setError(false);
    setQrLogoFileState(null);
  };

  const handleSaveClick = async () => {
    if (!qrLogoFileState) return;
    try {
      let formData = new FormData();
      formData.append("qr_icon", qrLogoFileState);
      formData.append("project_id", projectId);
      await uploadQrIcon(formData);
      setHasExistingQr(true);
      setIsEditing(false);
      setLocalPreview(null);
      setQrLogoFileState(null);
    } catch {
      setError(true);
      setIsEditing(false);
    }
  };

  const handleQrRemove = () => {
    setQrLogoFileState(null);
    setQrPreviewUrl('');
    let formData = new FormData();
    formData.append("project_id", projectId);
    removeQrIcon(formData);
  };

  if (isProjectLoading) {
    return (
      <Grid container sx={{ ml: 2, mt: "16px" }}>
        <Grid item xs={12}>
          <Skeleton variant="rectangular" height={70} width="100%" />
        </Grid>
      </Grid>
    );
  }

  // ✅ Determine which image to show for preview
  const displayQrImage =
    localPreview ||
    qrIcon ||
    projectDetails?.qr_icon_url ||
    null;

  return (
    <Grid container sx={{ ml: 2, mt: "16px" }}>
      <Grid
        container
        xs={12}
        sm={12}
        md={12}
        lg={12}
        xl={12}
        sx={{ alignSelf: "center", display: "flex", mt: 1 }}
      >
        <Grid item xs={6} sm={6} md={4} lg={4} xl={4} sx={{ alignSelf: "center" }}>
          <Typography sx={{ fontWeight: "bold", alignSelf: "center", fontSize: 14 }}>
            Upload QR Code Logo
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6} md={6}>
          {/* --- CASE 1: No QR uploaded --- */}
          {!hasExistingQr && !localPreview && !isEditing && (
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <Button
                  variant="outlined"
                  component="label"
                  size="small"
                  sx={{ textTransform: "none", fontWeight: 500 }}
                >
                  Browse
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleFileUpload}
                  />
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  size="small"
                  disabled
                  sx={{ textTransform: "none", fontWeight: 500 }}
                >
                  Save
                </Button>
              </Grid>
            </Grid>
          )}

          {/* --- CASE 2: File selected but not yet saved --- */}
          {!hasExistingQr && localPreview && (
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <Box
                  component="img"
                  src={localPreview}
                  alt="QR preview"
                  sx={{
                    width: 70,
                    height: 70,
                    objectFit: "contain",
                    borderRadius: 1,
                    border: "1px solid #c4c4c4",
                    padding: 0.2,
                  }}
                />
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSaveClick}
                  disabled={uploadingQrIcon}
                  startIcon={
                    uploadingQrIcon && (
                      <ThreeDots height="20" width="20" radius="9" color="#fff" />
                    )
                  }
                  sx={{ textTransform: "none", fontWeight: 500 }}
                >
                  {uploadingQrIcon ? "Saving" : "Save"}
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  onClick={handleCancelClick}
                  disabled={uploadingQrIcon}
                  sx={{ textTransform: "none", fontWeight: 500 }}
                >
                  Cancel
                </Button>
              </Grid>
            </Grid>
          )}

          {/* --- CASE 3: Already saved (view/edit/remove mode) --- */}
          {hasExistingQr && !isEditing && (
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <Box
                  component="img"
                  src={displayQrImage}
                  alt="QR Logo preview"
                  sx={{
                    width: 70,
                    height: 70,
                    objectFit: "contain",
                    borderRadius: 1,
                    border: "1px solid #c4c4c4",
                    padding: 0.2,
                  }}
                />
              </Grid>

              <Grid item>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setIsQrModalOpen(true)}
                  sx={{ textTransform: "none", fontWeight: 500 }}
                >
                  View
                </Button>
              </Grid>

              <Grid item>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleEditClick}
                  sx={{ textTransform: "none", fontWeight: 500 }}
                >
                  Edit
                </Button>
              </Grid>

              <Grid item>
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  onClick={handleQrRemove}
                  sx={{ textTransform: "none", fontWeight: 500 }}
                  disabled={uploadingQrIcon || removingQrIcon}
                  startIcon={
                    removingQrIcon && (
                      <ThreeDots height="20" width="20" radius="9" color="#fff" />
                    )
                  }
                >
                  {removingQrIcon ? "Removing" : "Remove"}
                </Button>
              </Grid>
            </Grid>
          )}

          {/* --- CASE 4: Editing existing QR --- */}
          {isEditing && (
            <Grid container spacing={2} alignItems="center">
              {!localPreview ? (
                <>
                  <Grid item>
                    <Button
                      variant="outlined"
                      component="label"
                      size="small"
                      sx={{ textTransform: "none", fontWeight: 500 }}
                    >
                      Browse
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={handleFileUpload}
                      />
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      variant="contained"
                      size="small"
                      disabled
                      sx={{ textTransform: "none", fontWeight: 500 }}
                    >
                      Save
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      onClick={handleCancelClick}
                      sx={{ textTransform: "none", fontWeight: 500 }}
                    >
                      Cancel
                    </Button>
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item>
                    <Box
                      component="img"
                      src={localPreview}
                      alt="New QR preview"
                      sx={{
                        width: 70,
                        height: 70,
                        objectFit: "contain",
                        borderRadius: 1,
                        border: "1px solid #c4c4c4",
                        padding: 0.2,
                      }}
                    />
                  </Grid>
                  <Grid item>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleSaveClick}
                      disabled={uploadingQrIcon}
                      startIcon={
                        uploadingQrIcon && (
                          <ThreeDots height="20" width="20" radius="9" color="#fff" />
                        )
                      }
                      sx={{ textTransform: "none", fontWeight: 500 }}
                    >
                      {uploadingQrIcon ? "Saving" : "Save"}
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      onClick={handleCancelClick}
                      sx={{ textTransform: "none", fontWeight: 500 }}
                    >
                      Cancel
                    </Button>
                  </Grid>
                </>
              )}
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
}
